import React, { useEffect, useMemo, useState } from 'react';
import { AutocompleteRenderInputParams } from '@material-ui/lab';
import { Grid, Avatar, Typography, CircularProgress, Box } from '@material-ui/core';
import { makeStyles, Theme } from '@material-ui/core/styles';
import cx from 'classnames';
import profilePlaceholder from 'img/photo-placeholder.png';
import Services from 'services/network';
import { getInitials } from 'helpers/common';
import { PeakModules } from 'common/constants/peakModules';
import { debounce } from 'common/utils';
import { INamedEntity } from 'common/interfaces/common';
import { TextField } from 'common/components/index';
import { IEmployeeBasicInfo } from 'interfaces/employee.interfaces';
import { makeEmployeeLabel } from 'utils/employee.utils';
import { IPaginatedData } from 'common/interfaces/pagination';
import { useLazyGetSalespersons } from 'api/dictionary.api';
import Autocomplete from '../Autocomplete/Autocomplete';

interface UserAutocompleteProps {
  label: string | JSX.Element;
  fullWidth: boolean;
  error?: boolean;
  helperText?: React.ReactNode;
  value: any;
  onChange: (value) => void;
  renderOption?: (params) => React.ReactNode;
  module: PeakModules;
  requestOptions?: any;
  recommended?: boolean;
  isSalesperson?: boolean;
}

const useStyles = makeStyles((theme: Theme) => ({
  textFieldRoot: {
    '&.MuiInputBase-root.MuiOutlinedInput-root.MuiAutocomplete-inputRoot': {
      padding: '0 65px 0 0',
    },
    '& > .MuiInputBase-input.MuiOutlinedInput-input.MuiAutocomplete-input': {
      padding: '22px 12px 10px 12px',
    },
  },
  avatarWrap: {
    position: 'relative',
    flex: 'none',
    width: 40,
    height: 40,
    borderRadius: '50%',
    backgroundColor: theme.palette.secondary.light,
  },
  avatarInitial: {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translateX(-50%) translateY(-50%)',
    fontSize: '16px',
    fontWeight: 700,
    color: theme.palette.darkBackground?.light,
  },
}));

const UserAutocomplete = (props: UserAutocompleteProps): JSX.Element => {
  const {
    label,
    fullWidth,
    error,
    helperText,
    value,
    onChange,
    renderOption,
    module,
    requestOptions,
    recommended,
    isSalesperson,
  } = props;

  const [open, setOpen] = React.useState(false);
  const [options, setOptions] = React.useState<IEmployeeBasicInfo[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const classes = useStyles();

  const [getSalespersons] = useLazyGetSalespersons();

  const loadEmployees = useMemo(() => {
    return debounce(async () => {
      setIsLoading(true);

      let employeesPayload: IPaginatedData<IEmployeeBasicInfo>;

      if (isSalesperson) {
        employeesPayload = await getSalespersons({
          module,
          ...requestOptions,
        }).unwrap();
      } else {
        employeesPayload = await Services.DictionaryLists.getEmployeesList({
          module,
          ...requestOptions,
        });
      }

      setOptions(employeesPayload.data);

      setIsLoading(false);
    });
  }, [module, requestOptions, isSalesperson, getSalespersons]);

  useEffect(() => {
    if (!open) {
      setOptions([]);
    } else {
      loadEmployees();
    }
  }, [open, loadEmployees]);

  const renderUserOption = (employee: IEmployeeBasicInfo) => {
    const initials = getInitials(employee.firstName, employee.lastName);

    return (
      <Grid container spacing={1} alignItems="center">
        <Grid item>
          <Box className={cx(classes.avatarWrap)}>
            {initials && !employee.imageUrl ? (
              <Box className={classes.avatarInitial}>{initials}</Box>
            ) : (
              <Avatar src={employee.imageUrl || profilePlaceholder} alt={employee.firstName} />
            )}
          </Box>
        </Grid>
        <Grid item>
          <Typography variant="subtitle2">{makeEmployeeLabel(employee)}</Typography>
        </Grid>
      </Grid>
    );
  };

  return (
    <Autocomplete
      open={open}
      value={value}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      getOptionSelected={(option: INamedEntity, valueEmployee: INamedEntity) => {
        return option.id === valueEmployee.id;
      }}
      getOptionLabel={(employee: IEmployeeBasicInfo | null) => {
        if (employee) {
          return makeEmployeeLabel(employee);
        }

        return '';
      }}
      renderOption={renderOption || renderUserOption}
      onChange={(e, data) => onChange(data)}
      options={options}
      loading={isLoading}
      inputValue={
        value
          ? `${
              value?.employeeIdentifier ? `${value?.employeeIdentifier} - ` : ''
            }${value?.firstName || ''} ${value?.lastName || ''}`
          : ''
      }
      renderInput={(params: AutocompleteRenderInputParams) => {
        return (
          <TextField
            {...params}
            label={label}
            variant="outlined"
            fullWidth={fullWidth}
            error={error}
            helperText={helperText}
            inputProps={{
              ...params.inputProps,
              autoComplete: 'new-password',
              form: {
                autoComplete: 'off',
              },
            }}
            // eslint-disable-next-line react/jsx-no-duplicate-props
            InputProps={{
              classes: {
                root: classes.textFieldRoot,
              },
              ...params.InputProps,
              endAdornment: (
                <>
                  {isLoading ? <CircularProgress color="primary" size={20} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
            recommended={recommended}
          />
        );
      }}
    />
  );
};

export default UserAutocomplete;
